/**
 * Remember the last list querystring per pathname.
 * Needed because in-app Back/Breadcrumb links often navigate to a bare path
 * (e.g. /trip_requests) and would otherwise drop URL filters.
 * Browser Back still works from history; this restores intentional SPA returns.
 *
 * Session memory is cleared when the user leaves that list's CRUD workflow
 * (another Get All / module). Explicit "Show All" also clears. Logout clears all.
 */
import { findListCrudScope } from "./listCrudScopes";

const LIST_SEARCH_PREFIX = "crm:listSearch:";
const LIST_LABELS_PREFIX = "crm:listFilterLabels:";

const storageKey = (pathname) =>
  `${LIST_SEARCH_PREFIX}${String(pathname || "").replace(/\/$/, "") || "/"}`;

const labelsStorageKey = (pathname) =>
  `${LIST_LABELS_PREFIX}${String(pathname || "").replace(/\/$/, "") || "/"}`;

export const rememberListSearch = (pathname, search = "") => {
  try {
    const clean = String(search || "").replace(/^\?/, "");
    sessionStorage.setItem(storageKey(pathname), clean);
  } catch {
    // private mode / quota — URL remains the primary source
  }
};

export const readRememberedListSearch = (pathname) => {
  try {
    return sessionStorage.getItem(storageKey(pathname)) || "";
  } catch {
    return "";
  }
};

export const clearRememberedListSearch = (pathname) => {
  try {
    sessionStorage.removeItem(storageKey(pathname));
  } catch {
    // ignore
  }
};

/** Build `/path?a=1` from last remembered filters (or bare path). */
export const getRememberedListPath = (pathname) => {
  const base = String(pathname || "/");
  const search = readRememberedListSearch(base);
  if (!search) return base;
  return `${base}?${search}`;
};

/**
 * Like getRememberedListPath but merges/overrides specific query keys
 * (e.g. bonds SideNav tabs: type=receipt_voucher | withdrawal_voucher).
 */
export const getRememberedListPathWithOverrides = (pathname, overrides = {}) => {
  const base = String(pathname || "/").replace(/\/$/, "") || "/";
  const params = new URLSearchParams(readRememberedListSearch(base));

  Object.entries(overrides).forEach(([key, value]) => {
    params.delete(key);
    params.delete(`${key}[]`);
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      value
        .map((item) => String(item).trim())
        .filter(Boolean)
        .forEach((item) => params.append(`${key}[]`, item));
      return;
    }
    params.set(key, String(value));
  });

  const search = params.toString();
  return search ? `${base}?${search}` : base;
};

/**
 * Persist AsyncSelect labels for ID-based filters.
 * URL only stores IDs; labels are restored when returning to the list.
 */
export const rememberListFilterLabels = (pathname, labels = {}) => {
  try {
    sessionStorage.setItem(labelsStorageKey(pathname), JSON.stringify(labels || {}));
  } catch {
    // ignore
  }
};

export const readListFilterLabels = (pathname) => {
  try {
    const raw = sessionStorage.getItem(labelsStorageKey(pathname));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const patchListFilterLabel = (pathname, field, entry) => {
  const current = readListFilterLabels(pathname);
  if (Array.isArray(entry?.items)) {
    const items = entry.items
      .filter((item) => item?.id && item?.name)
      .map((item) => ({ id: String(item.id), name: String(item.name) }));
    if (!items.length) {
      if (current[field] != null) {
        delete current[field];
        rememberListFilterLabels(pathname, current);
      }
      return;
    }
    rememberListFilterLabels(pathname, {
      ...current,
      [field]: { items },
    });
    return;
  }
  if (!entry?.id || !entry?.name) {
    if (current[field] != null) {
      delete current[field];
      rememberListFilterLabels(pathname, current);
    }
    return;
  }
  rememberListFilterLabels(pathname, {
    ...current,
    [field]: { id: String(entry.id), name: String(entry.name) },
  });
};

export const clearListFilterLabels = (pathname) => {
  try {
    sessionStorage.removeItem(labelsStorageKey(pathname));
  } catch {
    // ignore
  }
};

/** Clear remembered query + AsyncSelect labels for one list path. */
export const clearRememberedListState = (pathname) => {
  clearRememberedListSearch(pathname);
  clearListFilterLabels(pathname);
};

/** Clear all remembered list filters/labels (logout / full reset). */
export const clearAllRememberedListState = () => {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (
        key &&
        (key.startsWith(LIST_SEARCH_PREFIX) || key.startsWith(LIST_LABELS_PREFIX))
      ) {
        keys.push(key);
      }
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
};

/**
 * When navigation leaves a CRUD workflow (e.g. Clients Get All → Trip Requests),
 * drop that workflow's session memory so SideNav / in-app return starts clean.
 * Staying inside the same CRUD (list → view → edit) keeps filters.
 * @returns {boolean} true if any remembered list state was cleared
 */
export const clearListStateOnLeaveCrud = (previousPathname, nextPathname) => {
  const prev = String(previousPathname || "");
  const next = String(nextPathname || "");
  if (!prev || prev === next) return false;

  const prevScope = findListCrudScope(prev);
  if (!prevScope) return false;

  // Keep filters when navigating to a path still covered by the same workflow
  // (e.g. account statement → linked sales invoice details).
  if (typeof prevScope.isInScope === "function" && prevScope.isInScope(next)) {
    return false;
  }

  const nextScope = findListCrudScope(next);
  if (nextScope && nextScope.id === prevScope.id) return false;

  prevScope.listPaths.forEach((listPath) => {
    clearRememberedListState(listPath);
  });
  return true;
};
